export const CURRENT_SITE_SCHEMA_VERSION = 2;

export type OnboardingOperatingMode = 'team' | 'front_desk';
export type OnboardingPlanKey =
  | 'solo' | 'duo' | 'trio' | 'squad' | 'studio' | 'team' | 'enterprise' | 'frontDesk';
export type OnboardingStepId = 'mode' | 'availability' | 'service' | 'site' | 'payment' | 'goLive';
export type SiteExperience = 'full_site' | 'booking_only';

export type TenantLifecycleState = 'onboarding' | 'active' | 'suspended' | 'disabled' | 'archived';

export type PublicBusinessHours = Record<
  'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday',
  string
>;

export type SiteDraft = {
  experience: SiteExperience;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
  };
  business: {
    displayName: string;
    timezone: string;
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
    googlePlaceId?: string;
    businessHours: PublicBusinessHours;
  };
  homepage: {
    heroTitle: string;
    heroSubtitle: string;
    about?: string;
    mapUrl?: string;
    navbarLogoVisible: boolean;
    spotlightLogoVisible: boolean;
    logoPlacement?: 'navbar' | 'heroOverlay' | 'both';
    /**
     * What fills the spotlight background. Missing means image, so drafts saved
     * before hero video still render their hero image. A video always uses
     * assets.hero as its poster and fallback.
     */
    heroMediaType?: 'image' | 'video';
    gallery: Array<{ image: string; title?: string; description?: string }>;
    reviews: Array<{ name: string; text: string; rating: number }>;
    /** Absent on drafts saved before the FAQ section existed. */
    faq?: Array<{ question: string; answer: string }>;
  };
  assets: {
    logo?: string;
    hero?: string;
    /** MP4 in this tenant's site-assets folder; shown only when heroMediaType is video. */
    heroVideo?: string;
    heroLogo?: string;
    favicon?: string;
    sharingImage?: string;
  };
  socials: { facebook?: string; instagram?: string; twitter?: string };
  sections: {
    showTeam: boolean;
    showReviews: boolean;
    showGallery: boolean;
    showAbout: boolean;
    showFaq?: boolean;
  };
  booking: {
    enabled: boolean;
    showPrices: boolean;
    showEmployeePicker: boolean;
    ctaLabel: string;
  };
  metadata: { title: string; description: string; canonicalUrl?: string };
};

export type PublishValidationIssue = {
  code: string;
  path?: string;
  message: string;
  severity: 'error' | 'warning';
};

export type PublishValidationResult = {
  valid: boolean;
  errors: PublishValidationIssue[];
  warnings: PublishValidationIssue[];
};

export type TenantSiteDraft = {
  tenantId: string;
  schemaVersion: number;
  draftVersion: number;
  content: SiteDraft;
  updatedAt: string;
  updatedBy: string;
};

export type UpdateTenantSiteDraftResponse = {
  tenantId: string;
  draftVersion: number;
  updatedAt: string;
  // Preview mode only: whether this save also updated the public preview.
  preview?: { published: boolean; issues: string[] };
};

export type PublishTenantSiteResponse = {
  tenantId: string;
  publishedAt: string;
  publicUrl?: string;
  warnings: PublishValidationIssue[];
};

export type TenantViewerAccess = {
  uid: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
};

export type TenantPublishStatus = {
  tenantId: string;
  lifecycleState: TenantLifecycleState;
  publishedAt?: string;
  publishedDraftVersion?: number;
  publicUrl?: string;
  configSource: 'legacy' | 'published';
  founderPlanKey?: OnboardingPlanKey;
  // Preview mode: enabled is the switch; available while no owner has signed up;
  // live once the public "coming soon" site is being served.
  salesPreview?: { enabled: boolean; available: boolean; live: boolean; siteUrl?: string };
  // ownerEmail and ownerName are missing until a super admin adds the owner.
  invitation?: { ownerEmail?: string; ownerName?: string; accessCode: string; expiresAt: string };
  readiness: {
    ownerClaimed: boolean;
    sitePublished: boolean;
    subscriptionReady: boolean;
    subscriptionBypassed: boolean;
    configurationReady: boolean;
    primaryHostnameReady: boolean;
    canActivate: boolean;
    blockers: string[];
  };
  viewer: TenantViewerAccess;
};

export type ProvisionTenantInput = {
  tenantId: string;
  displayName: string;
  slug: string;
  timezone: string;
  businessType?: string;
  ownerName: string;
  ownerEmail: string;
  hostname: string;
  planKey: OnboardingPlanKey;
  resourceSingular?: string;
  resourcePlural?: string;
};

export type ProvisionTenantResponse = {
  tenantId: string;
  ownerName: string;
  ownerEmail: string;
  ownerInviteCode: string;
  hostname: string;
  planKey: OnboardingPlanKey;
  lifecycleState: 'onboarding';
};

export type UpdateTenantResourceTerminologyInput = {
  tenantId: string;
  resourceSingular: string;
  resourcePlural: string;
};

export type UpdateTenantResourceTerminologyResponse = UpdateTenantResourceTerminologyInput & {
  updatedAt: string;
};

export type ActivateTenantResponse = {
  tenantId: string;
  hostname: string;
  lifecycleState: 'active';
};

export type OnboardingGate = {
  complete: boolean;
  label: string;
  route: string;
  detail: string;
};

export type OnboardingStep = {
  id: OnboardingStepId;
  label: string;
  complete: boolean;
  started: boolean;
  current: boolean;
  locked: boolean;
};

export type SaveMyOnboardingModeInput = {
  operatingMode: OnboardingOperatingMode;
  resourceSingular?: string;
  resourcePlural?: string;
  resourceCount?: number;
  bookingSubject: 'people' | 'resources';
  scheduleManagement: 'individual' | 'centralized';
  customerSelection: 'specific' | 'automatic';
};

export type ConfirmMyOnboardingPlanInput = { teamSize: number };

export type OnboardingBreakInput = { start: string; end: string };

export type SaveMyOnboardingAvailabilityInput = {
  days: Array<keyof PublicBusinessHours>;
  hours: Partial<Record<keyof PublicBusinessHours, { start: string; end: string }>>;
  bookingsStartDate: string;
  resourceNames?: string[];
  generalBreak?: OnboardingBreakInput;
  breaks?: Partial<Record<keyof PublicBusinessHours, OnboardingBreakInput | null>>;
};

// Answers Scheduify prepared from the client's logo, flyers or website, for the
// owner to confirm. See docs/site-from-client-materials.md.
export type OnboardingPreparedAnswers = {
  mode?: SaveMyOnboardingModeInput;
  availability?: Omit<SaveMyOnboardingAvailabilityInput, 'bookingsStartDate'>;
  decisions: Array<{ topic: string; choice: string; reason: string; source: string }>;
  serviceCount: number;
};

export type TenantOnboardingStatus = {
  tenantId: string;
  lifecycleState: TenantLifecycleState;
  hostname: string;
  loginUrl: string;
  publicUrl?: string;
  businessType?: string;
  business: {
    displayName: string;
    timezone: string;
    businessType?: string;
  };
  introductionComplete: boolean;
  businessConfirmed: boolean;
  serviceGuideComplete: boolean;
  siteIntroductionComplete: boolean;
  siteSetupConfirmed: boolean;
  prepared?: OnboardingPreparedAnswers;
  siteSetupConfirmedDraftVersion?: number;
  siteSetupConfirmedAt?: string;
  bookingChoice?: 'specific_person' | 'business_assigned';
  requestedManagementStyle?: 'individual_logins' | 'centralized';
  bookingSubject?: 'people' | 'resources';
  scheduleManagement?: 'individual' | 'centralized';
  managementStyle?: 'individual_logins' | 'centralized';
  teamSize?: number;
  operatingMode?: OnboardingOperatingMode;
  recommendedPlanKey?: OnboardingPlanKey;
  selectedPlanKey?: OnboardingPlanKey;
  resourceSingular?: string;
  resourcePlural?: string;
  resourceCount?: number;
  resourcesProvisioned?: boolean;
  incompleteResourceNames?: string[];
  siteExperience?: SiteExperience;
  steps: OnboardingStep[];
  gates: {
    availability: OnboardingGate;
    service: OnboardingGate;
    payment: OnboardingGate;
    sitePublished: OnboardingGate;
  };
  completedCount: number;
  requiredCount: number;
  canActivate: boolean;
};

export type PublishingErrorCode =
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'INVALID_ARGUMENT'
  | 'NOT_FOUND'
  | 'DRAFT_VERSION_CONFLICT'
  | 'IDEMPOTENCY_CONFLICT'
  | 'PUBLISH_VALIDATION_FAILED'
  | 'UNSUPPORTED_SCHEMA'
  | 'INTERNAL'
  | 'UNKNOWN';

export class TenantPublishingError extends Error {
  constructor(
    public readonly code: PublishingErrorCode,
    message: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'TenantPublishingError';
  }
}
